import type { Scene, Choice, DecisionLogEntry } from '@/types';
import {
  useCharacterStore,
  useWorldStore,
  useNarrativeStore,
} from '@/engine/state';
import { escenaRequerida, obtenerEscena } from './sceneRegistry';
import { aplicarEfectos } from './effects';
import { evaluarCondiciones, motivoCondicionNoCumplida } from './conditions';
import type { ConditionContext } from './conditions';
import { programarConsecuencia, resolverConsecuencia } from '@/engine/consequences';

/**
 * Arma un snapshot de contexto para evaluar condiciones. Se hace
 * al momento de decidir, no al entrar a la escena, para que los
 * efectos recién aplicados queden reflejados.
 */
export function snapshotContexto(): ConditionContext {
  const personaje = useCharacterStore.getState().personaje;
  const { flags, reputacion } = useWorldStore.getState();
  return {
    personaje: {
      stats: personaje.stats,
      skillsDesbloqueadas: personaje.skillsDesbloqueadas,
      hechizosAprendidos: personaje.hechizosAprendidos,
      inventario: personaje.inventario,
    },
    flags,
    reputacion,
  };
}

export interface OpcionAnotada {
  choice: Choice;
  habilitada: boolean;
  /** Si está atenuada, el motivo (el primero que falla) para mostrar al jugador. */
  motivoNoDisponible: string | null;
  /** Si debe ocultarse cuando no se cumple la condición. */
  oculta: boolean;
}

/**
 * Anota las opciones de una escena con si están habilitadas/ocultas.
 * El orden se preserva; la UI decide si renderiza las ocultas (no debería).
 */
export function opcionesAnotadas(scene: Scene, ctx?: ConditionContext): OpcionAnotada[] {
  const contexto = ctx ?? snapshotContexto();
  return scene.opciones.map((choice) => {
    const habilitada = evaluarCondiciones(choice.condiciones, contexto);
    if (habilitada) {
      return { choice, habilitada: true, motivoNoDisponible: null, oculta: false };
    }
    const motivo =
      choice.condiciones
        ?.map((c) => motivoCondicionNoCumplida(c, contexto))
        .find((m): m is string => m !== null) ?? null;
    const oculta = (choice.alFallarCondicion ?? 'atenuar') === 'ocultar';
    return { choice, habilitada: false, motivoNoDisponible: motivo, oculta };
  });
}

/**
 * Entra a una escena: aplica onEnter, resuelve consequences cuyo trigger
 * coincide con el id de esta escena, actualiza el sceneIdActual.
 */
export function entrarAEscena(sceneId: string): Scene {
  const scene = escenaRequerida(sceneId);
  aplicarEfectos(scene.onEnter);

  // Resolver consequences que disparen en esta escena.
  const world = useWorldStore.getState();
  for (const pendiente of [...world.consecuenciasPendientes]) {
    if (pendiente.triggeredBy === sceneId) {
      // Por defecto usamos el resumenOpaco como impacto final si no se define
      // uno explícito por otro mecanismo. Escenas pueden resolver manualmente
      // via un effect custom en fases siguientes.
      resolverConsecuencia(
        pendiente.id,
        pendiente.resumenOpaco.replace(/^Próximamente/i, 'Resuelto'),
      );
    }
  }

  const narrative = useNarrativeStore.getState();
  narrative.irAEscena(sceneId);

  if (scene.capitulo !== narrative.capituloActual) {
    narrative.setCapitulo(scene.capitulo);
  }

  if (scene.final) {
    narrative.marcarFinal(scene.final);
  }

  return scene;
}

/**
 * Resultado detallado de tomar una opción.
 */
export interface ResultadoDecision {
  destino: string;
  resumenEfectos: string[];
  logEntry: DecisionLogEntry;
}

/**
 * Elige una opción: aplica sus efectos, registra la decisión en el log
 * (con vinculo a consecuencias diferidas si corresponde) y entra a la
 * escena destino. Devuelve la info de la decisión.
 */
export function elegirOpcion(
  scene: Scene,
  choiceId: string,
): ResultadoDecision {
  const choice = scene.opciones.find((c) => c.id === choiceId);
  if (!choice) {
    throw new Error(`Opción "${choiceId}" no existe en la escena "${scene.id}"`);
  }

  const ctx = snapshotContexto();
  if (!evaluarCondiciones(choice.condiciones, ctx)) {
    throw new Error(`Opción "${choiceId}" no cumple sus condiciones.`);
  }

  const resumenes = aplicarEfectos(choice.efectos);

  const entryId = `${scene.id}:${choice.id}:${Date.now()}`;

  // Buscamos scheduleConsequence entre los efectos, para armar el link.
  let consequencePendiente: string | undefined;
  if (choice.efectos) {
    for (const e of choice.efectos) {
      if (e.kind === 'scheduleConsequence') {
        consequencePendiente = e.consequenceId;
        programarConsecuencia({
          consequenceId: e.consequenceId,
          triggeredBy: resolverTriggerDefecto(e.consequenceId, choice.destino),
          resumenOpaco: 'Próximamente lo sabrás…',
          decisionEntryId: entryId,
        });
        break;
      }
    }
  }

  const logEntry: DecisionLogEntry = {
    id: entryId,
    timestamp: Date.now(),
    sceneId: scene.id,
    choiceId: choice.id,
    textoOpcion: choice.texto,
    impactoInmediato: resumenes.length > 0 ? resumenes.join(' · ') : undefined,
    consequencePendiente,
  };

  useNarrativeStore.getState().registrarDecision(logEntry);

  entrarAEscena(choice.destino);

  return {
    destino: choice.destino,
    resumenEfectos: resumenes,
    logEntry,
  };
}

/**
 * Si el consequenceId sigue la convención `trigger:<sceneId>:resto`,
 * extraemos la escena disparadora. Si no, usamos la escena destino
 * como default razonable.
 */
function resolverTriggerDefecto(consequenceId: string, destinoDefault: string): string {
  const m = /^trigger:([^:]+)/.exec(consequenceId);
  return m?.[1] ?? destinoDefault;
}

export { obtenerEscena, escenaRequerida };
